/**
 * v3.superAdmin — Wave 6.3 SuperAdmin Records Management write surface.
 *
 * Routes (server: PCBJxChangeApi/Functions/SuperAdminRecordsFunction.cs,
 * shipped by sibling WAVE-6.3-BACKEND agent):
 *
 *   GET    /v3/super-admin/records/browse              query filters
 *   POST   /v3/super-admin/records/purge                hard-delete one record
 *   POST   /v3/super-admin/records/purge-bulk           hard-delete many
 *   POST   /v3/super-admin/records/correct              bulk metadata fix
 *   POST   /v3/super-admin/records/test-mode-wipe       wipe all isTestMode rows
 *   GET    /v3/super-admin/records/purge-history        searchable audit
 *   GET    /v3/super-admin/records/recovery             SP-snapshot search
 *   POST   /v3/super-admin/records/recover              restore one snapshot
 *   POST   /v3/super-admin/records/retention-hold       add hold
 *   DELETE /v3/super-admin/records/retention-hold/{id}  remove hold
 *
 * All routes require SuperAdmin role + the `x-config-seed-bypass` header
 * (mirrors Wave 6.2 admin write surfaces).
 *
 * Composed onto `client.retail.superAdmin` from `client.ts`.
 */

import type {
  RequestOptions,
  ApiResult,
  PurgeHistoryRow,
  RecordsBrowseFilters,
  RecordsBrowseRow,
  RecordsHardDeleteInput,
  RecordsHardDeleteResponse,
  RecordsHardDeleteBulkInput,
  RecordsBulkCorrectInput,
  RecordsBulkCorrectResponse,
  RecordsTestModeWipeInput,
  RecordsTestModeWipeResponse,
  RecordsRecoveryFilters,
  RecordsRecoveryRow,
  RecoverFromSnapshotInput,
  RecoverFromSnapshotResponse,
  RetentionHold,
  RetentionHoldRequest,
} from '@retail-ops/shared-types';
import type { ApiHttpClient } from '../http.js';
import { buildAdminAuthHeaders } from '../admin-auth.js';

function withBypass(
  bypassKey: string,
  opts?: RequestOptions,
): RequestOptions {
  return {
    ...opts,
    headers: buildAdminAuthHeaders(opts?.headers, bypassKey),
  };
}

class SuperAdminRecordsApi {
  public constructor(private readonly http: ApiHttpClient) {}

  /** GET /v3/super-admin/records/browse */
  public browse(
    filters?: RecordsBrowseFilters,
    opts?: RequestOptions,
  ): Promise<ApiResult<RecordsBrowseRow[]>> {
    return this.http.get<ApiResult<RecordsBrowseRow[]>>('/v3/super-admin/records/browse', {
      ...opts,
      ...(filters
        ? { query: filters as unknown as Record<string, string | number | boolean> }
        : {}),
    });
  }

  /** POST /v3/super-admin/records/purge — single-record hard delete. */
  public purge(
    bypassKey: string,
    body: RecordsHardDeleteInput,
    opts?: RequestOptions,
  ): Promise<RecordsHardDeleteResponse> {
    return this.http.post<RecordsHardDeleteResponse>(
      '/v3/super-admin/records/purge',
      { ...withBypass(bypassKey, opts), body },
    );
  }

  /** POST /v3/super-admin/records/purge-bulk — multi-record hard delete. */
  public purgeBulk(
    bypassKey: string,
    body: RecordsHardDeleteBulkInput,
    opts?: RequestOptions,
  ): Promise<RecordsHardDeleteResponse> {
    return this.http.post<RecordsHardDeleteResponse>(
      '/v3/super-admin/records/purge-bulk',
      { ...withBypass(bypassKey, opts), body },
    );
  }

  /** POST /v3/super-admin/records/correct — bulk metadata correction. */
  public correct(
    bypassKey: string,
    body: RecordsBulkCorrectInput,
    opts?: RequestOptions,
  ): Promise<RecordsBulkCorrectResponse> {
    return this.http.post<RecordsBulkCorrectResponse>(
      '/v3/super-admin/records/correct',
      { ...withBypass(bypassKey, opts), body },
    );
  }

  /** POST /v3/super-admin/records/test-mode-wipe — clears all isTestMode rows. */
  public testModeWipe(
    bypassKey: string,
    body: RecordsTestModeWipeInput,
    opts?: RequestOptions,
  ): Promise<RecordsTestModeWipeResponse> {
    return this.http.post<RecordsTestModeWipeResponse>(
      '/v3/super-admin/records/test-mode-wipe',
      { ...withBypass(bypassKey, opts), body },
    );
  }

  /** GET /v3/super-admin/records/purge-history — searchable audit log. */
  public purgeHistory(
    filters?: { startDate?: string; endDate?: string; entityType?: string; top?: number },
    opts?: RequestOptions,
  ): Promise<ApiResult<PurgeHistoryRow[]>> {
    return this.http.get<ApiResult<PurgeHistoryRow[]>>(
      '/v3/super-admin/records/purge-history',
      {
        ...opts,
        ...(filters
          ? { query: filters as unknown as Record<string, string | number | boolean> }
          : {}),
      },
    );
  }

  /** GET /v3/super-admin/records/recovery — search SP snapshot library. */
  public recovery(
    filters?: RecordsRecoveryFilters,
    opts?: RequestOptions,
  ): Promise<ApiResult<RecordsRecoveryRow[]>> {
    return this.http.get<ApiResult<RecordsRecoveryRow[]>>(
      '/v3/super-admin/records/recovery',
      {
        ...opts,
        ...(filters
          ? { query: filters as unknown as Record<string, string | number | boolean> }
          : {}),
      },
    );
  }

  /** POST /v3/super-admin/records/recover — restore one record from SP snapshot. */
  public recoverFromSnapshot(
    bypassKey: string,
    body: RecoverFromSnapshotInput,
    opts?: RequestOptions,
  ): Promise<RecoverFromSnapshotResponse> {
    return this.http.post<RecoverFromSnapshotResponse>(
      '/v3/super-admin/records/recover',
      { ...withBypass(bypassKey, opts), body },
    );
  }

  /** GET /v3/super-admin/records/retention-holds */
  public listRetentionHolds(opts?: RequestOptions): Promise<ApiResult<RetentionHold[]>> {
    return this.http.get<ApiResult<RetentionHold[]>>(
      '/v3/super-admin/records/retention-holds',
      { ...opts },
    );
  }

  /** POST /v3/super-admin/records/retention-hold — add a retention hold. */
  public addRetentionHold(
    bypassKey: string,
    body: RetentionHoldRequest,
    opts?: RequestOptions,
  ): Promise<ApiResult<RetentionHold>> {
    return this.http.post<ApiResult<RetentionHold>>(
      '/v3/super-admin/records/retention-hold',
      { ...withBypass(bypassKey, opts), body },
    );
  }

  /** DELETE /v3/super-admin/records/retention-hold/{cartId} — release a hold. */
  public removeRetentionHold(
    bypassKey: string,
    cartId: string,
    opts?: RequestOptions,
  ): Promise<ApiResult<{ released: boolean }>> {
    return this.http.del<ApiResult<{ released: boolean }>>(
      `/v3/super-admin/records/retention-hold/${encodeURIComponent(cartId)}`,
      { ...withBypass(bypassKey, opts) },
    );
  }
}

/** Composite — exposed at `client.retail.superAdmin`. */
export class SuperAdminApi {
  public readonly records: SuperAdminRecordsApi;

  public constructor(http: ApiHttpClient) {
    this.records = new SuperAdminRecordsApi(http);
  }
}
